import React from "react";
import { useGet } from "../../common/hooks";
import { Division, GoalieStatsResponse } from "../../common/interfaces";
import { Divider, Breadcrumb, Menu } from "antd";
import _ from "lodash";
import { Link, useParams } from "react-router-dom";
import { CompactTable } from "../../common/components/CompactTable";
import Spinner from "../../common/components/Spinner";
import { getDivisionName } from "../../common/utils";
import { goalieStatsColumns } from "../../common/goalieStatsColumns";

const DIVISIONS = Object.values(Division)
  .filter(v => typeof v === "number" && v !== Division.UNKNOWN) as Division[];

// No Goalie object available here (just the flattened GoalieTeamGames row from the stats endpoint),
// so this can't use the entity-based buildGoalieDisplayName/getGoalieDisplayNameWithSubLabel in utils.ts.
function withSubPrefix(name: string, isSub: boolean): string {
  return isSub ? `(Sub) ${name}` : name;
}

export function GoalieStats() {
  const { division: divisionParam } = useParams<{ division?: string }>();
  const division = DIVISIONS.find(d => getDivisionName(d).toLowerCase() === divisionParam?.toLowerCase());

  const stats = useGet<GoalieStatsResponse>(
    division !== undefined ? `/api/goalieStats?division=${getDivisionName(division).toLowerCase()}` : undefined
  );

  const divisionMenu = (
    <Menu>
      {DIVISIONS.map(d => (
        <Menu.Item key={d}>
          <Link to={`/goaliestats/${getDivisionName(d).toLowerCase()}`}>{getDivisionName(d)}</Link>
        </Menu.Item>
      ))}
    </Menu>
  );

  const breadcrumb = (
    <Breadcrumb>
      <Breadcrumb.Item>NCWHL</Breadcrumb.Item>
      <Breadcrumb.Item overlay={divisionMenu}>
        {division !== undefined ? getDivisionName(division) : "Select a division"}
      </Breadcrumb.Item>
    </Breadcrumb>
  );

  if (division === undefined || stats === undefined || stats.data === undefined) {
    return (
      <>
        {breadcrumb}
        <Divider>Goalie Stats</Divider>
        {division !== undefined && <Spinner size={"large"} />}
      </>
    );
  }

  const sortedGoalieStats = _.sortBy(stats.data.goalieStats, g => g.name);

  // Pivot the flat (goalie, team, gamesPlayed) rows into a grid: one row per goalie,
  // one column per team in the division.
  const teamColumns = stats.data.goalieTeamStats
    ? _.sortBy(_.uniqBy(stats.data.goalieTeamStats, "teamId"), "teamName")
    : undefined;

  const goalieTeamStatsColumns = teamColumns && [
    {
      dataIndex: "goalieName",
      title: "Goalie",
      width: 200
    },
    ...teamColumns.map(t => ({
      dataIndex: `team_${t.teamId}`,
      title: t.teamName
    }))
  ];

  const goalieTeamGrid = stats.data.goalieTeamStats &&
    teamColumns &&
    _.orderBy(
      Object.values(_.groupBy(stats.data.goalieTeamStats, "goalieId")).map(rows => {
        // A goalie who wasn't active this season is a sub; label them and sort them below active goalies.
        const isSub = !rows[0].active;
        const row: { [key: string]: string | number | boolean } = {
          goalieId: rows[0].goalieId,
          goalieName: withSubPrefix(rows[0].goalieName, isSub),
          isSub
        };
        teamColumns.forEach(t => {
          const match = rows.find(r => r.teamId === t.teamId);
          row[`team_${t.teamId}`] = match ? match.gamesPlayed : 0;
        });
        return row;
      }),
      ["isSub", "goalieName"]
    );

  return (
    <>
      {breadcrumb}
      <Divider>Goalie Stats</Divider>
      <CompactTable
        data={sortedGoalieStats}
        columns={goalieStatsColumns}
        loading={stats.isLoading}
        rowKey={"goalieId"}
      />
      {goalieTeamGrid && goalieTeamStatsColumns && (
        <>
          <Divider style={{ marginTop: 40 }}>Games Played by Team</Divider>
          <CompactTable
            data={goalieTeamGrid}
            columns={goalieTeamStatsColumns}
            loading={stats.isLoading}
            rowKey={"goalieId"}
          />
        </>
      )}
    </>
  );
}
